import { Stroke, Style } from "ol/style";
import { spectralColor } from "./util/colorScale";
import { getLinearTagFlowStyle } from "./linearStyling";
import { LineString } from "ol/geom";
import { lrDecode, getLengthCm } from "./linearStyling";
import { CURVATURE_HIGHWAY_TYPES } from "./supportedHighwayTypes";
import { asArray } from "ol/color";
import { CURVATURE_LINEAR_RECONSTRUCTED_TYPE } from "../addfeaturehandlers/CurvatureLinearHandler";
import { getProperty, getPropertyEndingWith } from "./propertyExtraction";
import {
  getLaneDirectionArrowStyles,
  LANE_TYPES,
} from "./util/laneDirectionArrows";

const rdylgnInterpolator = (t) => {
  const [r, g, b] = spectralColor(t);
  return `rgb(${r},${g},${b})`;
};

// Max lateral acceleration assumed when deriving a comfortable speed from
// curvature. 0.3g ≈ 2.94 m/s².
const MAX_LATERAL_ACCELERATION_MS2 = 3.0;

// Orbis curvature (physical curvature * 10000, right = negative) -> NDS Live
// 10-bit curvature (0-1023, centered at 511, right = >511). The two domains
// use opposite signs, so the sign is flipped once, then a progressively
// coarser piecewise scale is applied as |c| grows. Table per
// https://tomtom.atlassian.net/wiki/spaces/AMF/pages/836405587 (see also the
// nds-orbis-curvature skill, which implements the same table in Python).
export function orbisCurvatureToNds(orbisCurvature) {
  const c = -(orbisCurvature / 10000);
  const absC = Math.abs(c);
  const sign = c >= 0 ? 1 : -1;
  let v;
  if (absC < 0.00064) {
    v = 511 + Math.round(c * 100000);
  } else if (absC < 0.00192) {
    v = 511 + sign * 32 + Math.round((c * 100000) / 2);
  } else if (absC < 0.00448) {
    v = 511 + sign * 80 + Math.round((c * 100000) / 4);
  } else if (absC < 0.0096) {
    v = 511 + sign * 136 + Math.round((c * 100000) / 8);
  } else if (absC < 0.01984) {
    v = 511 + sign * 196 + Math.round((c * 100000) / 16);
  } else if (absC < 0.04032) {
    v = 511 + sign * 258 + Math.round((c * 100000) / 32);
  } else if (absC < 0.08128) {
    v = 511 + sign * 321 + Math.round((c * 100000) / 64);
  } else if (absC < 0.16192) {
    v = 511 + sign * 384.5 + Math.round((c * 100000) / 128);
  } else {
    v = 511 + sign * 511;
  }
  return Math.trunc(Math.max(0, Math.min(1023, v)));
}

// Lateral-acceleration-derived speed (km/h) from a TomTom curvature value.
// radiusMeters = 10000 / |curvature|. Capped at 130 km/h.
export function maxSpeedFromCurvature(curvature) {
  if (curvature === 0) {
    return 130;
  }
  const radius = 10000 / Math.abs(curvature);
  const maxSpeedMs = Math.sqrt(MAX_LATERAL_ACCELERATION_MS2 * radius);
  return Math.min(Math.round(maxSpeedMs * 3.6), 130);
}

// Display string "X km/h (at Y m/s² lateral acceleration)" — surfaces the
// design lateral-accel threshold used to derive the speed. When the result
// hits the 130 km/h cap, the lateral-accel threshold was not the limiter,
// so we surface that instead.
export function maxSpeedDisplayFromCurvature(curvature) {
  const speedKmh = maxSpeedFromCurvature(curvature);
  if (speedKmh >= 130) {
    return `${speedKmh} km/h (capped at 130 km/h speed limit)`;
  }
  return `${speedKmh} km/h (at ${MAX_LATERAL_ACCELERATION_MS2.toFixed(2)} m/s² lateral acceleration)`;
}

// Display rows for a `curvature_at_junction` value — the same three rows the
// WKT viewer renders on hover. Empty array if missing or zero.
export function curvatureAtJunctionKeyValues(curvatureAtJunction) {
  if (curvatureAtJunction === undefined || curvatureAtJunction === null) {
    return [];
  }
  const curvature = Number(curvatureAtJunction);
  if (curvature === 0 || isNaN(curvature)) return [];
  const radius = 10000 / Math.abs(curvature);
  return [
    ["curvature_at_junction", curvature],
    ["NDS curvature", orbisCurvatureToNds(curvature)],
    ["radius", Math.round(radius) + " m"],
    ["max speed (based on curvature)", maxSpeedDisplayFromCurvature(curvature)],
  ];
}

export function getCurvatureStyleNormalOrbisSD(feature, resolution) {
  return getCurvatureColorStyle(
    feature,
    false,
    false,
    rdylgnInterpolator,
    resolution,
  );
}
export function getCurvatureStyleClickOrbisSD(feature, resolution) {
  return getCurvatureColorStyle(
    feature,
    true,
    false,
    rdylgnInterpolator,
    resolution,
  );
}
export function getCurvatureStyleHoverOrbisSD(feature, resolution) {
  return getCurvatureColorStyle(
    feature,
    false,
    true,
    rdylgnInterpolator,
    resolution,
  );
}
export function getCurvatureStyleNormalOrbisHD(feature, resolution) {
  return getCurvatureColorStyle(
    feature,
    false,
    false,
    rdylgnInterpolator,
    resolution,
  );
}
export function getCurvatureStyleClickOrbisHD(feature, resolution) {
  return getCurvatureColorStyle(
    feature,
    true,
    false,
    rdylgnInterpolator,
    resolution,
  );
}
export function getCurvatureStyleHoverOrbisHD(feature, resolution) {
  return getCurvatureColorStyle(
    feature,
    false,
    true,
    rdylgnInterpolator,
    resolution,
  );
}
export function getCurvatureStyleNormalGenesis(feature, resolution) {
  return getCurvatureColorStyle(
    feature,
    false,
    false,
    rdylgnInterpolator,
    resolution,
  );
}
export function getCurvatureStyleClickGenesis(feature, resolution) {
  return getCurvatureColorStyle(
    feature,
    true,
    false,
    rdylgnInterpolator,
    resolution,
  );
}
export function getCurvatureStyleHoverGenesis(feature, resolution) {
  return getCurvatureColorStyle(
    feature,
    false,
    true,
    rdylgnInterpolator,
    resolution,
  );
}

function getCurvatureColorStyle(
  feature,
  click,
  hover,
  colorInterpolator,
  resolution,
) {
  let width, backgroundColor, backgroundWidth;

  // Style for reconstructed curvature-linear curves
  const featureType = feature.get("type");
  if (featureType === CURVATURE_LINEAR_RECONSTRUCTED_TYPE) {
    let color = colorInterpolator(1);
    const bgColor = asArray("white").slice();
    bgColor[3] = 0.6;
    if (hover || click) {
      return [
        new Style({
          zIndex: 10,
          stroke: new Stroke({ color: bgColor, width: 8 }),
        }),
        new Style({
          zIndex: 11,
          stroke: new Stroke({ color: color, width: 4, lineDash: [5, 5] }),
        }),
      ];
    }
    return [
      new Style({
        zIndex: 8,
        stroke: new Stroke({ color: bgColor, width: 6 }),
      }),
      new Style({
        zIndex: 9,
        stroke: new Stroke({ color: color, width: 3, lineDash: [5, 5] }),
      }),
    ];
  }

  let hdmapType = feature.get("hdmap_type");
  if (hdmapType === undefined) {
    hdmapType = getPropertyEndingWith(feature, "-hdmap");
  }
  const osmHighway = feature.get("osm-highway");

  if (
    hdmapType !== undefined &&
    hdmapType !== "lane_center_line" &&
    hdmapType !== "lane_trajectory_line"
  ) {
    return;
  }

  const laneDirection = LANE_TYPES.includes(hdmapType)
    ? getProperty(feature, "lane_direction")
    : undefined;

  let orbisCurvature = feature.get("orbis-curvature");
  if (orbisCurvature === undefined) {
    orbisCurvature = feature.get("orbis-curvature:linear");
  }
  if (orbisCurvature === undefined) {
    orbisCurvature = feature.get("orbis-curvature:linear");
  }
  if (orbisCurvature === undefined) {
    orbisCurvature = getPropertyEndingWith(feature, "-curvature:linear");
  }
  if (orbisCurvature === undefined) {
    let color;
    if (osmHighway === undefined) {
      color = "grey";
    } else if (!CURVATURE_HIGHWAY_TYPES.includes(osmHighway)) {
      color = "black";
    } else {
      color = "grey";
    }

    backgroundColor = "white";
    width = 1;
    backgroundWidth = 4;
    const arrows = getLaneDirectionArrowStyles(
      feature,
      laneDirection,
      "black",
      resolution,
    );
    if (hover || click) {
      return [
        new Style({
          zIndex: 0,
          stroke: new Stroke({
            color: backgroundColor,
            width: backgroundWidth,
          }),
        }),
        new Style({
          zIndex: 1,
          stroke: new Stroke({
            color: color,
            width: width,
          }),
        }),
        ...arrows,
      ];
    }
    return [
      new Style({
        zIndex: 1,
        stroke: new Stroke({
          color: color,
          width: width,
        }),
      }),
      ...arrows,
    ];
  } else {
    if (hover) {
      backgroundColor = "black";
    } else if (click) {
      backgroundColor = "orange";
    } else {
      backgroundColor = "black";
    }
    if (hover || click) {
      width = 7;
      backgroundWidth = 10;
    } else {
      width = 5;
      backgroundWidth = 9;
    }

    const flowStyle = getLinearTagFlowStyle(
      orbisCurvature,
      width,
      feature,
      colorInterpolator,
      "curvature",
      7,
    );

    const styles = [
      flowStyle,
      new Style({
        zIndex: 6,
        stroke: new Stroke({
          color: backgroundColor,
          width: backgroundWidth,
        }),
      }),
    ];

    // Bars at each curvature LR element (value data point), not at each geometry coordinate
    const geometry = feature.getGeometry();
    if (geometry && geometry.getType && geometry.getType() === "LineString") {
      const lenCm = getLengthCm(feature);
      if (lenCm > 0) {
        const lrChain = lrDecode("bla:linear", orbisCurvature);
        const offsets = lrChain?.elements
          ?.map((el) => el.getStartDistance() / lenCm)
          .filter((t) => Number.isFinite(t))
          .map((t) => Math.min(1, Math.max(0, t)));

        if (offsets && offsets.length) {
          const line = /** @type {LineString} */ (geometry);

          // helper: point + tangent at normalized offset along the line
          const pointAndTangentAt = (t) => {
            if (t <= 0) {
              const a = line.getCoordinateAt(0);
              const b = line.getCoordinateAt(1e-6);
              return { x: a[0], y: a[1], dx: b[0] - a[0], dy: b[1] - a[1] };
            }
            if (t >= 1) {
              const a = line.getCoordinateAt(1);
              const b = line.getCoordinateAt(1 - 1e-6);
              return { x: a[0], y: a[1], dx: a[0] - b[0], dy: a[1] - b[1] };
            }
            const p = line.getCoordinateAt(t);
            const p0 = line.getCoordinateAt(Math.max(0, t - 1e-6));
            const p1 = line.getCoordinateAt(Math.min(1, t + 1e-6));
            return { x: p[0], y: p[1], dx: p1[0] - p0[0], dy: p1[1] - p0[1] };
          };

          for (const t of offsets) {
            const { x, y, dx, dy } = pointAndTangentAt(t);

            const mag = Math.hypot(dx, dy);
            if (!mag) continue;
            const ux = dx / mag;
            const uy = dy / mag;

            // perpendicular unit vector
            const px = -uy;
            const py = ux;

            // Screen-pixel length (not map units) so it can't stretch out far enough
            // at high zoom to cross into the lane-direction arrows offset from the
            // same line (see ARROW_OFFSET_PX in laneDirectionArrows.js). Sized
            // relative to backgroundWidth (the line's casing) so it always pokes a
            // few px past the casing on each side instead of being swallowed by it.
            const barLengthPx = backgroundWidth + 8;
            const half = (barLengthPx * (resolution || 1)) / 2;

            const x1 = x + px * half;
            const y1 = y + py * half;
            const x2 = x - px * half;
            const y2 = y - py * half;

            styles.push(
              new Style({
                geometry: new LineString([
                  [x1, y1],
                  [x2, y2],
                ]),
                stroke: new Stroke({
                  color: "rgba(0, 0, 0, 0.5)",
                  width: 2,
                }),
              }),
            );
          }
        }
      }
    }

    styles.push(
      ...getLaneDirectionArrowStyles(
        feature,
        laneDirection,
        "black",
        resolution,
      ),
    );

    return styles;
  }
}
